import { ICreateFtmfRequest, ITrackingDiagnostic } from "types/models/TrackingDiagnosticsModel";
import { BaseStoreClass, IBaseStoreClass } from "./BaseStoreClass";
import { INotificationsStore } from "./NotificationsStore";
import { action, makeObservable, observable } from "mobx";
import { getErrorMessage } from "utilities/ErrorHelper";
import { assignTrackingDiagnosticsSessionTypeColors, assignTrackingDiagnosticsTypeColors } from 'utilities/SystemUtils';
import { ITrackingDiagnosticsService } from "services/TrackingDiagnosticsService";
import { t } from 'i18next';

export interface ITrackingDiagnosticsStore extends IBaseStoreClass {
  trackingDiagnostics: ITrackingDiagnostic[];
  trackingDiagnosticsTypes: string[];
  sessionTypes: string[];
  currentSelectedTrackingDiagnostic: ITrackingDiagnostic | null;
  allSelectedTrackingDiagnosticIds: string[] | null;

  isLoadingTrackingDiagnostics: boolean;
  isRequestingFtmf: boolean;

  isBusy: boolean;

  setCurrentSelectedTrackingDiagnostic(trackingDiagnostic: ITrackingDiagnostic | null): void;
  getTrackingDiagnostics(): void;
  cancelSelectedEntries(): Promise<void>;
  requestFtmf(request: ICreateFtmfRequest): Promise<boolean>;
}

export class TrackingDiagnosticsStore extends BaseStoreClass implements ITrackingDiagnosticsStore {
  trackingDiagnostics: ITrackingDiagnostic[] = [];
  trackingDiagnosticsTypes: string[] = [];
  sessionTypes: string[] = [];
  currentSelectedTrackingDiagnostic: ITrackingDiagnostic | null = null;
  allSelectedTrackingDiagnosticIds: string[] | null = [];

  isLoadingTrackingDiagnostics: boolean = false;
  isRequestingFtmf: boolean = false;

  isBusy: boolean = this.isLoadingTrackingDiagnostics;

  _notificationsStore: INotificationsStore;
  _trackingDiagnosticsService: ITrackingDiagnosticsService;

  constructor(
    trackingDiagnosticsService: ITrackingDiagnosticsService,
    notificationsStore: INotificationsStore) {
    super();

    this.take = 100;

    this._notificationsStore = notificationsStore;
    this._trackingDiagnosticsService = trackingDiagnosticsService;

    makeObservable(this, {
      trackingDiagnostics: observable,
      trackingDiagnosticsTypes: observable,
      sessionTypes: observable,
      allSelectedTrackingDiagnosticIds: observable,
      isLoadingTrackingDiagnostics: observable,
      isRequestingFtmf: observable,
      isBusy: observable,
      currentSelectedTrackingDiagnostic: observable,
      setCurrentSelectedTrackingDiagnostic: action,
      getTrackingDiagnostics: action,
      cancelSelectedEntries: action,
      requestFtmf: action,
    });
  }

  async getTrackingDiagnostics() {
    try {
      this.setIsLoadingTrackingDiagnostics(true);

      const trackingDiagnosticsResponse = await this._trackingDiagnosticsService.getAllTrackingDiagnostics({
        page: this.page - 1, //Backend Pagination starts from 0
        pageSize: this.take,
        updatedAfter: "2025-10-01",
        includeDeleted: false
      });

      let trackingDiagnosticsDtoData: ITrackingDiagnostic[] = trackingDiagnosticsResponse.data;
      if (!trackingDiagnosticsDtoData || trackingDiagnosticsResponse.isError) {
        return;
      }

      let destictTypes = Array.from(new Set(trackingDiagnosticsDtoData.map(item => item.type)));
      let destictSessionTypes = Array.from(new Set(trackingDiagnosticsDtoData.map(item => item.sessionType)));

      this.setTrackingDiagnostics(trackingDiagnosticsDtoData);
      this.setTrackingDiagnosticsTypes(destictTypes);
      this.setTrackingDiagnosticsSessionTypes(destictSessionTypes);
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsLoadingTrackingDiagnostics(false);
    }
  }

  // =========================== UTILS ===========================

  updateIsBusy() {
    this.isBusy = this.isLoadingTrackingDiagnostics;
  }

  setIsLoadingTrackingDiagnostics(status: boolean) {
    this.isLoadingTrackingDiagnostics = status;
    this.updateIsBusy()
  }

  setTrackingDiagnostics(trackingDiagnostics: ITrackingDiagnostic[]) {
    this.trackingDiagnostics = trackingDiagnostics;
  }

  setTrackingDiagnosticsTypes(types: string[]) {
    this.trackingDiagnosticsTypes = types;
    assignTrackingDiagnosticsTypeColors(types);
  }

  setTrackingDiagnosticsSessionTypes(sessionTypes: string[]) {
    this.sessionTypes = sessionTypes;
    assignTrackingDiagnosticsSessionTypeColors(sessionTypes);
  }

  setCurrentSelectedTrackingDiagnostic = (trackingDiagnostic: ITrackingDiagnostic | null) => {
    this.currentSelectedTrackingDiagnostic = trackingDiagnostic;
  };

  addSelectedTrackingDiagnosticList = (trackingDiagnostic: ITrackingDiagnostic) => {
    if (!trackingDiagnostic || trackingDiagnostic.cancelledAtUtc != null) return;
    const current = this.allSelectedTrackingDiagnosticIds ?? [];
    const exists = current.includes(trackingDiagnostic.id);
    if (!exists) {
      this.allSelectedTrackingDiagnosticIds = [...current, trackingDiagnostic.id];
    }
  };

  removeSelectedTrackingDiagnosticList = (trackingDiagnostic: ITrackingDiagnostic) => {
    const current = this.allSelectedTrackingDiagnosticIds ?? [];
    const filtered = current.filter(id => id !== trackingDiagnostic.id);
    this.allSelectedTrackingDiagnosticIds = filtered;
  };

  addRangeSelectedTrackingDiagnosticList = (trackingDiagnostics: ITrackingDiagnostic[]) => {
    if (!trackingDiagnostics || trackingDiagnostics.length === 0) return;
    const current = this.allSelectedTrackingDiagnosticIds ?? [];
    const ids = new Set(current);
    const toAdd = trackingDiagnostics.filter(td => td.cancelledAtUtc == null).map(td => td.id).filter(id => !ids.has(id));
    if (toAdd.length > 0) {
      this.allSelectedTrackingDiagnosticIds = [...current, ...toAdd];
    }
  };

  removeRangeSelectedTrackingDiagnosticList = (trackingDiagnostics: ITrackingDiagnostic[]) => {
    if (!trackingDiagnostics || trackingDiagnostics.length === 0) return;
    const idsToRemove = new Set(trackingDiagnostics.map(td => td.id));
    const current = this.allSelectedTrackingDiagnosticIds ?? [];
    const filtered = current.filter(id => !idsToRemove.has(id));
    this.allSelectedTrackingDiagnosticIds = filtered;
  };

  clearSelectedTrackingDiagnosticList = () => {
    if (!this.allSelectedTrackingDiagnosticIds || this.allSelectedTrackingDiagnosticIds.length === 0) return;
    this.allSelectedTrackingDiagnosticIds = [];
  };

  requestFtmf = async (request: ICreateFtmfRequest): Promise<boolean> => {
    try {
      this.isRequestingFtmf = true;
      await this._trackingDiagnosticsService.createTrackingDiagnostics(request);
      this._notificationsStore.addToast(t('TrackingDiagnostics.RequestFtmf.Toast.SuccessTitle'), t('TrackingDiagnostics.RequestFtmf.Toast.SuccessMessage'), 'success');
      return true;
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
      return false;
    } finally {
      this.isRequestingFtmf = false;
    }
  };

  cancelSelectedEntries = async () => {
    const selectedIds = this.allSelectedTrackingDiagnosticIds ?? [];
    if (selectedIds.length === 0) return;

    const entriesToCancel = this.trackingDiagnostics.filter(td => selectedIds.includes(td.id));
    const failed: string[] = [];
    const BATCH_SIZE = 5;

    for (let i = 0; i < entriesToCancel.length; i += BATCH_SIZE) {
      const batch = entriesToCancel.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(td => this._trackingDiagnosticsService.cancelEntry(td.bundleId, td.id))
      );
      results.forEach((result, index) => {
        if (result.status === 'rejected') failed.push(batch[index].id);
      });
    }

    if (failed.length > 0) {
      this._notificationsStore.addToast('Error', `Failed to cancel ${failed.length} entry(s).`, 'error');
    }

    this.clearSelectedTrackingDiagnosticList();
    await this.getTrackingDiagnostics();
  };
}